import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, User, Phone, Mail, MessageSquare, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const AuctionEnquiryModal = ({ isOpen, onClose, propertyTitle }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // OTP States
    const [otp, setOtp] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [isSendingOtp, setIsSendingOtp] = useState(false);
    const [resendTimer, setResendTimer] = useState(0);

    // Timer logic for OTP resend
    React.useEffect(() => {
        let interval = null;
        if (resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [resendTimer]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'phone') {
            const cleanValue = value.replace(/\D/g, '').slice(0, 10);
            setFormData(prev => ({ ...prev, [name]: cleanValue }));
            if (otpSent || otpVerified) {
                setOtpSent(false);
                setOtpVerified(false);
                setOtp('');
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSendOtp = async (e) => {
        if (e) e.preventDefault();

        if (!formData.phone || formData.phone.length !== 10) {
            toast.error('Please enter a valid 10-digit phone number');
            return;
        }

        setIsSendingOtp(true);
        try {
            const response = await axios.post('/api/otp/send', { phone: formData.phone });
            if (response.data.success) {
                setOtpSent(true);
                setResendTimer(60);
                toast.success('OTP sent successfully!');
            } else {
                toast.error(response.data.message || 'Failed to send OTP');
            }
        } catch (error) {
            console.error('OTP Send error:', error);
            toast.error(error.response?.data?.message || 'Error sending OTP');
        } finally {
            setIsSendingOtp(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        if (e) e.preventDefault();

        if (!otp || otp.length !== 6) {
            toast.error('Please enter 6-digit OTP');
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await axios.post('/api/otp/verify', {
                phone: formData.phone,
                otp
            });

            if (response.data.success) {
                setOtpVerified(true);
                toast.success('OTP verified successfully!');
            } else {
                toast.error(response.data.message || 'Invalid or expired OTP');
            }
        } catch (error) {
            console.error('OTP Verify error:', error);
            toast.error(error.response?.data?.message || 'Error during verification');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();

        if (!otpVerified) {
            toast.error('Please verify your phone number first');
            return;
        }

        setIsSubmitting(true);
        const loadingToast = toast.loading('Submitting your enquiry...');

        try {
            const response = await axios.post('/api/auction-enquiry/submit', {
                ...formData,
                property_title: propertyTitle
            });

            if (response.data.success) {
                toast.success('Enquiry submitted successfully!', { id: loadingToast });
                navigate('/thank-you');
                onClose();
            } else {
                toast.error(response.data.message || 'Failed to submit enquiry.', { id: loadingToast });
            }
        } catch (error) {
            console.error('Error submitting enquiry:', error);
            toast.error(error.response?.data?.message || 'Failed to submit enquiry. Please try again.', { id: loadingToast });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setFormData({ name: '', phone: '', email: '', message: '' });
        setOtp('');
        setOtpSent(false);
        setOtpVerified(false);
        setResendTimer(0);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 10 }}
                    className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="bg-primary-900 text-white p-8">
                        <button
                            onClick={handleClose}
                            className="absolute top-6 right-6 text-white/60 hover:text-white transition-all p-2 hover:bg-white/10 rounded-full"
                        >
                            <X className="w-6 h-6" />
                        </button>
                        <h3 className="text-3xl font-extrabold mb-2 tracking-tight">Enquire Us</h3>
                        <div className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 mt-0.5 text-primary-300" />
                            <p className="text-primary-100 text-sm font-medium leading-tight">Property: {propertyTitle}</p>
                        </div>
                    </div>

                    <div className="p-8 max-h-[80vh] overflow-y-auto custom-scrollbar">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Full Name */}
                            <div>
                                <div className="mb-2">
                                    <label className="block text-base font-bold text-gray-800">Full Name *</label>
                                    <p className="text-xs text-gray-500 font-medium">Applicant / Borrower name</p>
                                </div>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                                        <User className="w-5 h-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                                    </div>
                                    <input
                                        type="text"
                                        name="name"
                                        required
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Enter applicant / borrower name"
                                        className="w-full pl-12 pr-4 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:ring-4 focus:ring-primary-100 focus:border-primary-500 outline-none transition-all font-medium text-gray-700 placeholder:text-gray-400"
                                    />
                                </div>
                            </div>

                            {/* Registered Mobile Number */}
                            <div>
                                <div className="mb-2">
                                    <label className="block text-base font-bold text-gray-800">Phone Number *</label>
                                    <p className="text-xs text-gray-500 font-medium">Primary contact for updates</p>
                                </div>
                                <div className="flex gap-3">
                                    <div className="relative flex-1 group">
                                        <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                                            <Phone className="w-5 h-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                                        </div>
                                        <input
                                            type="tel"
                                            name="phone"
                                            required
                                            value={formData.phone}
                                            onChange={handleChange}
                                            placeholder="Enter 10-digit mobile number"
                                            maxLength={10}
                                            disabled={otpVerified}
                                            className={`w-full pl-12 pr-4 py-4 bg-gray-50/50 border rounded-2xl focus:ring-4 focus:ring-primary-100 focus:border-primary-500 outline-none transition-all font-medium text-gray-700 placeholder:text-gray-400 disabled:bg-green-50/50 disabled:border-green-200 disabled:text-green-700 ${otpVerified ? 'border-green-300' : 'border-gray-200'}`}
                                        />
                                        {otpVerified && (
                                            <div className="absolute right-4 top-1/2 -translate-y-1/2">
                                                <CheckCircle2 className="h-5 w-5 text-green-600" />
                                            </div>
                                        )}
                                    </div>
                                    {!otpVerified && (
                                        <button
                                            type="button"
                                            onClick={handleSendOtp}
                                            disabled={isSendingOtp || formData.phone.length !== 10 || resendTimer > 0}
                                            className={`px-6 py-4 rounded-2xl font-bold transition-all shadow-sm flex items-center justify-center min-w-[120px] ${isSendingOtp || formData.phone.length !== 10 || resendTimer > 0
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                : 'bg-primary-600 text-white hover:bg-primary-700 hover:shadow-md'
                                                }`}
                                        >
                                            {isSendingOtp ? (
                                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                            ) : resendTimer > 0 ? (
                                                `Resend ${resendTimer}s`
                                            ) : otpSent ? (
                                                'Resend OTP'
                                            ) : (
                                                'Send OTP'
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* OTP Verification Block */}
                            {otpSent && !otpVerified && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="p-4 bg-primary-50/50 border border-primary-100 rounded-3xl"
                                >
                                    <div className="flex flex-col items-center gap-3">
                                        <div className="text-center">
                                            <h4 className="text-xs font-bold text-primary-900 uppercase tracking-widest mb-1">Verify OTP</h4>
                                            <p className="text-[10px] text-primary-600 font-medium italic">Enter 6-digit code sent to your phone</p>
                                        </div>
                                        <div className="flex w-full gap-2 items-center">
                                            <input
                                                type="text"
                                                value={otp}
                                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                                placeholder="000000"
                                                maxLength={6}
                                                className="flex-1 min-w-0 px-3 py-3.5 border border-primary-200 rounded-2xl focus:ring-4 focus:ring-primary-100 focus:border-primary-500 transition text-center text-xl tracking-[0.3em] font-black text-primary-900 bg-white shadow-inner"
                                            />
                                            <button
                                                type="button"
                                                onClick={handleVerifyOtp}
                                                disabled={otp.length !== 6 || isSubmitting}
                                                className={`px-5 py-3.5 rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-md active:scale-95 ${otp.length === 6 && !isSubmitting
                                                    ? 'bg-green-600 text-white hover:bg-green-700'
                                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
                                                    }`}
                                            >
                                                {isSubmitting ? '...' : 'Verify'}
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {/* Email Address */}
                            <div>
                                <div className="mb-2">
                                    <label className="block text-base font-bold text-gray-800">Email Address *</label>
                                    <p className="text-xs text-gray-500 font-medium">Acknowledgement & communication</p>
                                </div>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                                        <Mail className="w-5 h-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                                    </div>
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="Enter your email address"
                                        className="w-full pl-12 pr-4 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:ring-4 focus:ring-primary-100 focus:border-primary-500 outline-none transition-all font-medium text-gray-700 placeholder:text-gray-400"
                                    />
                                </div>
                            </div>

                            {/* Message */}
                            <div>
                                <div className="mb-2">
                                    <label className="block text-base font-bold text-gray-800">Message</label>
                                    <p className="text-xs text-gray-500 font-medium">Optional additional details</p>
                                </div>
                                <div className="relative group">
                                    <div className="absolute top-4 left-4 pointer-events-none">
                                        <MessageSquare className="w-5 h-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                                    </div>
                                    <textarea
                                        name="message"
                                        rows="3"
                                        value={formData.message}
                                        onChange={handleChange}
                                        placeholder="Tell us about your requirement..."
                                        className="w-full pl-12 pr-4 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:ring-4 focus:ring-primary-100 focus:border-primary-500 outline-none transition-all resize-none font-medium text-gray-700 placeholder:text-gray-400"
                                    ></textarea>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={isSubmitting || !otpVerified}
                                className={`w-full py-5 rounded-2xl font-black text-lg flex items-center justify-center gap-3 transition-all active:scale-95 shadow-xl ${isSubmitting || !otpVerified
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none'
                                    : 'bg-primary-600 hover:bg-primary-700 text-white hover:shadow-primary-200/50'
                                    }`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="w-6 h-6 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
                                        <span>Submitting...</span>
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-6 h-6" />
                                        <span>{!otpVerified ? 'Verify Phone to Submit' : 'Send Enquiry'}</span>
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default AuctionEnquiryModal;
